// Phase 9.1.B — Client portal login.
//
// Mirrors apps/web/src/pages/LoginPage but targets /portal/auth/login.
// Distinguishes 403 email_not_verified from 401 invalid_credentials so the
// UI can surface a "verify your email" hint with a resend link.
import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError, api } from "@/lib/api";
import { homeFor, isStoreOnlyUser } from "@/lib/portal-store";

interface LocationState {
  from?: string;
}

/** Where to go after sign-in: the page they were sent from, else their home.
 *  A store employee only ever lands on a store page. */
function landingFor(user: { roles: string[] }, state: LocationState | null): string {
  const from = state?.from;
  if (!from) return homeFor(user);
  if (isStoreOnlyUser(user) && !(from === "/store" || from.startsWith("/store/"))) return "/store";
  return from;
}

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resendStatus, setResendStatus] = useState<
    "idle" | "sending" | "sent"
  >("idle");

  // Already signed in — bounce home (a store employee's home is /store).
  if (user) {
    return <Navigate to={landingFor(user, location.state as LocationState | null)} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNeedsVerify(false);
    setSubmitting(true);
    try {
      const signedIn = await login(email, password);
      navigate(landingFor(signedIn, location.state as LocationState | null), { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        if (err.status === 403 && body?.error === "email_not_verified") {
          setNeedsVerify(true);
        } else if (err.status === 429) {
          setError("Too many attempts. Try again in a few minutes.");
        } else {
          setError("Email or password didn't match. Try again.");
        }
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    if (!email) return;
    setResendStatus("sending");
    try {
      await api("/portal/auth/resend-verification", {
        method: "POST",
        body: JSON.stringify({ email }),
        skipAuth: true,
      });
    } catch {
      // Service returns 202 regardless; swallow errors.
    } finally {
      setResendStatus("sent");
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream-50 px-6 py-16">
      <div className="w-full max-w-md">
        <h1 className="mb-2 font-display text-4xl font-bold tracking-tight">
          Welcome back
        </h1>
        <p className="mb-10 text-brown-400">
          Sign in to your other portal.
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-brown-500">
              Email
            </span>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
              autoComplete="email"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-brown-500">
              Password
            </span>
            <input
              type="password"
              required
              minLength={1}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
              autoComplete="current-password"
            />
          </label>

          {error && (
            <div className="rounded-md border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-500">
              {error}
            </div>
          )}

          {needsVerify && (
            <div className="rounded-md border border-peach-300 bg-peach-50 px-4 py-3 text-sm text-brown-500">
              <p className="mb-2 font-semibold">Email not verified yet.</p>
              <p className="mb-3">
                Check your inbox for the link we sent when you signed up.
              </p>
              {resendStatus === "idle" && (
                <button
                  type="button"
                  onClick={onResend}
                  className="text-sm font-semibold text-ink underline-offset-2 hover:underline"
                >
                  Resend verification email
                </button>
              )}
              {resendStatus === "sending" && (
                <span className="text-sm text-brown-400">Sending…</span>
              )}
              {resendStatus === "sent" && (
                <span className="text-sm text-brown-400">
                  Sent. Check your inbox.
                </span>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-ink px-4 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700 disabled:opacity-50"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-8 flex items-center justify-between text-sm text-brown-400">
          <Link
            to="/signup"
            className="font-semibold text-ink underline-offset-2 hover:underline"
          >
            Create an account
          </Link>
          <Link
            to="/forgot-password"
            className="text-brown-400 underline-offset-2 hover:text-brown-500 hover:underline"
          >
            Forgot password?
          </Link>
        </div>
      </div>
    </div>
  );
}
